import React, { useEffect, useState } from 'react';
import { Button, CardBody, CardHeader, Row, Col } from 'reactstrap';
import { useStateSelector } from '../../../../../store/selectors';
import './ProfilePreview.scss';
import { BuyerUploads } from '../BuyerUploads/BuyerUploads';
import { MarketplaceBuyerUploadAccessLevel } from '../../../../../slices/marketplace/buyer/models/buyer-upload';
import Helpers from '../../../../../utils/helper';
import { NdaTermPreferences } from '../PublicProfile/NdaTermPreferences/NdaTermPreferences';
import { Form } from '@availity/form';
import InfoTooltip from '../../../../../components/info-tooltip/info-tooltip';
import { locationTypeTooltipHtml } from '../../../../../slices/marketplace/timeline/models/enums/location-type.enum';
import { useServiceFocuses } from '../../../../../custom-hooks/useServiceFocuses';
import { mapServiceFocusesToOptions } from '../../../../UserPages/Login/Modals/PracticeRegistrationForm/utils';
import { useFeatureFlag } from '../../../../../custom-hooks/useFeatureFlag';
import { FeatureFlagsEnum } from '../../../../FeatureFlags/FeatureFlags.enum';
import { USER_ROLES } from '../../../../../utils/constants';
import { LegalIssuesForm } from '../../../../../components/LoiForm/LegalIssuesForm';
import { LoiFormMode } from '../../../../../slices/marketplace/loi-form/loi-form-mode.enum';
import { LoiForm } from '../../../../../slices/marketplace/loi-form/loi-form';
import { ADDITIONAL_NDA_COVENANTS_BUYER_TOOLTIP } from '../../../../../slices/marketplace/loi-form/loi-form-tooltips';

export interface ProfilePreviewProps {
    isReadonly: boolean;
    setIsEditMode?: (isEditMode: boolean) => void;
    showLoiTabs?: boolean;
    showLoiFormsInProfile?: boolean;
    loiFormOverride?: LoiForm | null;
}

export const ProfilePreview = (props: ProfilePreviewProps) => {
    const isFF_MrktplcBuyers_3_13Enabled = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcBuyers_3_13
    );
    const summary = useStateSelector((s) => s.userSummary.summary);
    const isAdmin = summary?.roleId === USER_ROLES.ADMIN;
    const isBuyerViewer = !!summary?.isMarketplaceBuyer;
    const serviceFocuses = useServiceFocuses();
    const targetBuyer = useStateSelector(
        (state) => state.buyersState.targetBuyer
    );
    const [formValues, setFormValues] = useState({});
    const [activeTab, setActiveTab] = useState(0);

    const effectiveLoiForm =
        props.loiFormOverride !== undefined
            ? props.loiFormOverride
            : targetBuyer?.profile?.loiForm;

    const formatList = (items: string[]): string => {
        if (!items || items.length === 0) return '';
        if (items.length === 1) return items[0];
        if (items.length === 2) return `${items[0]} and ${items[1]}`;
        return `${items.slice(0, -1).join(', ')} and ${
            items[items.length - 1]
        }`;
    };

    const legalDocumentInputs = targetBuyer?.profile?.legalDocumentInputs;
    const legalValue = (value: string) => value || 'N/A';
    const loiForm = effectiveLoiForm;
    const legalIssues = loiForm?.legalIssues ?? [];
    const additionalCovenants = loiForm?.additionalCovenants ?? [];
    const legalIssueSections = loiForm?.legalIssueSections ?? [];

    const legalIssueSectionsFiltered = legalIssueSections.filter((s) =>
        legalIssues.some((li) => li.sectionId === s.id)
    );
    const additionalCovenantSections = legalIssueSections.filter((s) =>
        additionalCovenants.some((ac) => ac.sectionId === s.id)
    );

    const isUploadsExist = (accessLevel: MarketplaceBuyerUploadAccessLevel) => {
        return (
            targetBuyer?.profile?.uploads?.filter(
                (x) => x.accessLevel === accessLevel
            ).length > 0
        );
    };

    useEffect(() => {
        if (targetBuyer?.profile?.currentNdaTermPreferences) {
            const ndaTermPreferencesState =
                targetBuyer?.profile?.currentNdaTermPreferences;
            setFormValues({
                nonDisclosurePeriod:
                    ndaTermPreferencesState?.nonDisclosurePeriod ?? '',
                nonSolicitationDvmNamesVisible:
                    ndaTermPreferencesState?.nonSolicitationDvmNamesVisible ??
                    '',
                nonSolicitationDvmNamesBlinded:
                    ndaTermPreferencesState?.nonSolicitationDvmNamesBlinded ??
                    '',
                nonSolicitationStaffNamesVisible:
                    ndaTermPreferencesState?.nonSolicitationStaffNamesVisible ??
                    '',
                nonSolicitationStaffNamesBlinded:
                    ndaTermPreferencesState?.nonSolicitationStaffNamesBlinded ??
                    '',
            });
        }
    }, [targetBuyer?.profile?.currentNdaTermPreferences]);

    const showLegalIssuesTab = legalIssues.length > 0;
    const showAdditionalCovenantsTab = additionalCovenants.length > 0;

    const tabs = [
        'Profile',
        ...(showLegalIssuesTab ? ['Legal Issues'] : []),
        ...(showAdditionalCovenantsTab ? ['Additional NDA Covenants'] : []),
    ];

    const profileInfoContent = (
        <>
            <Row>
                <Col md={4} className="left-panel">
                    <div className="buyer-info">
                        {' '}
                        <div className="general-information">
                            <div className="logo-preview">
                                <img src={targetBuyer?.profile?.logoUrl}></img>
                            </div>

                            <div className="section-details">
                                <div className="section-label">Buyer Name:</div>
                                <div className="section-value">
                                    {targetBuyer?.profile?.companyName}
                                </div>
                            </div>
                            <div className="section-details">
                                <div className="section-label">
                                    Buyer Website:
                                </div>
                                <div className="section-value">
                                    {targetBuyer?.profile?.website ? (
                                        <a
                                            href={
                                                targetBuyer?.profile?.website.startsWith(
                                                    'http'
                                                )
                                                    ? targetBuyer?.profile
                                                          ?.website
                                                    : `https://${targetBuyer?.profile?.website}`
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer">
                                            {targetBuyer?.profile?.website}
                                        </a>
                                    ) : (
                                        'N/A'
                                    )}
                                </div>
                            </div>

                            <div className="section-details column">
                                <div className="section-label">
                                    Description:
                                </div>
                                <div className="section-value">
                                    {targetBuyer?.profile?.companyDescription}
                                </div>
                            </div>
                        </div>
                        <div className="location-information">
                            <div className="section-details">
                                <div className="section-label">
                                    Acquision Focus:
                                </div>
                            </div>
                            <div className="section-details column">
                                <div className="section-label">
                                    Geographic Focus:
                                </div>

                                <div className="section-value">
                                    Region:{' '}
                                    {formatList(
                                        targetBuyer?.profile
                                            ?.stateAcquisitionRegion
                                    )}
                                </div>
                                <div className="section-value">
                                    Location Type:
                                    <InfoTooltip
                                        idText="location-type-preview-tooltip"
                                        text=""
                                        html={locationTypeTooltipHtml}
                                        place="top"
                                    />{' '}
                                    {formatList(
                                        targetBuyer?.profile?.geographyMarket
                                    )}
                                </div>
                            </div>
                            <div className="section-details practice-size-section column">
                                <div className="section-label">
                                    Practice Size: Min / "Sweet Spot" / Max
                                </div>

                                <div className="section-value">
                                    Revenue:{' '}
                                    {Helpers.currencyFormat(
                                        targetBuyer?.profile
                                            ?.targetPracticeSizeRevenueMin,
                                        false
                                    )}{' '}
                                    /{' '}
                                    {Helpers.currencyFormat(
                                        targetBuyer?.profile
                                            ?.targetPracticeSizeRevenueSweetspot,
                                        false
                                    )}{' '}
                                    /{' '}
                                    {Helpers.currencyFormat(
                                        targetBuyer?.profile
                                            ?.targetPracticeSizeRevenueMax,
                                        false
                                    )}
                                </div>
                                <div className="section-value">
                                    FTE DVMs:{' '}
                                    {
                                        targetBuyer?.profile
                                            ?.targetPracticeSizeFteMin
                                    }{' '}
                                    /{' '}
                                    {
                                        targetBuyer?.profile
                                            ?.targetPracticeSizeFteSweetspot
                                    }{' '}
                                    /{' '}
                                    {
                                        targetBuyer?.profile
                                            ?.targetPracticeSizeFteMax
                                    }
                                </div>
                                {(targetBuyer?.profile
                                    ?.targetPracticeNumberOfLocationsMin !=
                                    null ||
                                    targetBuyer?.profile
                                        ?.targetPracticeNumberOfLocationsMax !=
                                        null ||
                                    targetBuyer?.profile
                                        ?.targetPracticeNumberOfLocationsSweetspot !=
                                        null) && (
                                    <div className="section-value">
                                        Number of Locations:{' '}
                                        {targetBuyer?.profile
                                            ?.targetPracticeNumberOfLocationsMin ??
                                            '-'}{' '}
                                        /{' '}
                                        {targetBuyer?.profile
                                            ?.targetPracticeNumberOfLocationsSweetspot ??
                                            '-'}{' '}
                                        /{' '}
                                        {targetBuyer?.profile
                                            ?.targetPracticeNumberOfLocationsMax ??
                                            '-'}
                                    </div>
                                )}
                            </div>
                            {targetBuyer?.profile?.practiceFocuses?.length >
                            0 ? (
                                <div className="section-details column">
                                    <div className="section-label">
                                        Service Focus:
                                    </div>
                                    <div className="section-value">
                                        {serviceFocuses
                                            .filter((mapping) =>
                                                (
                                                    targetBuyer?.profile
                                                        ?.practiceFocuses ?? []
                                                ).some(
                                                    (buyerOption) =>
                                                        buyerOption ==
                                                        mapping.id
                                                )
                                            )
                                            .map((focusMapping, index, arr) => (
                                                <span
                                                    key={index}
                                                    className="practice-focus">
                                                    {
                                                        mapServiceFocusesToOptions(
                                                            serviceFocuses
                                                        )[focusMapping.id]
                                                    }
                                                    {index < arr.length - 1
                                                        ? ', '
                                                        : ''}
                                                </span>
                                            ))}
                                    </div>
                                </div>
                            ) : (
                                []
                            )}
                        </div>
                    </div>
                </Col>
                <Col md={8} className="right-panel">
                    <div className="historical-map-preview">
                        <img src={targetBuyer?.profile?.growthMapUrl}></img>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <CardHeader className="buyer-uploads-header space-between">
                        Data NDA Terms Preferences
                    </CardHeader>
                    <CardBody className="nda-term-preferences-section">
                        {targetBuyer?.profile?.currentNdaTermPreferences && (
                            <Form
                                initialValues={formValues}
                                enableReinitialize={true}
                                onSubmit={() => {}}>
                                <NdaTermPreferences
                                    isFullScreen={false}
                                    isSaving={false}
                                    isReadonly={true}
                                />
                            </Form>
                        )}
                    </CardBody>
                </Col>
            </Row>
            {isAdmin && legalDocumentInputs && (
                <>
                    <Row>
                        <Col md={12}>
                            <CardHeader className="buyer-uploads-header space-between">
                                Legal Documents Inputs
                            </CardHeader>
                            <CardBody className="pt-3">
                                <div className="section-details">
                                    <div className="section-label">
                                        Full Corporate Legal Name:
                                    </div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.fullCorporateLegalName
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">
                                        Type of Legal Entity:
                                    </div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.typeOfLegalEntity
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">
                                        Legal Entity State of Registration:
                                    </div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.legalEntityStateOfRegistration
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">
                                        DBA Full Name:
                                    </div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.dbaFullName
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">
                                        DBA Nickname:
                                    </div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.dbaNickName
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">
                                        Main Corp Phone:
                                    </div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.mainCorpPhone
                                        )}
                                    </div>
                                </div>
                            </CardBody>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <CardHeader className="buyer-uploads-header">
                                Main Corp Address
                            </CardHeader>
                            <CardBody className="pt-3">
                                <div className="section-details">
                                    <div className="section-label">
                                        Address Line 1:
                                    </div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.addressLine1
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">
                                        Address Line 2:
                                    </div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.addressLine2
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">City:</div>
                                    <div className="section-value">
                                        {legalValue(legalDocumentInputs.city)}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">State:</div>
                                    <div className="section-value">
                                        {legalValue(legalDocumentInputs.state)}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">
                                        Zip Code:
                                    </div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.zipCode
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">
                                        Country:
                                    </div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.country
                                        )}
                                    </div>
                                </div>
                            </CardBody>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12}>
                            <CardHeader className="buyer-uploads-header">
                                Legal Notices Contact Information
                            </CardHeader>
                            <CardBody className="pt-3">
                                <div className="section-details">
                                    <div className="section-label">Name:</div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.contactName
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">Title:</div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.contactTitle
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">Email:</div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.contactEmail
                                        )}
                                    </div>
                                </div>
                                <div className="section-details">
                                    <div className="section-label">Phone:</div>
                                    <div className="section-value">
                                        {legalValue(
                                            legalDocumentInputs.contactPhone
                                        )}
                                    </div>
                                </div>
                            </CardBody>
                        </Col>
                    </Row>
                </>
            )}
            {isUploadsExist(MarketplaceBuyerUploadAccessLevel.Public) && (
                <Row>
                    <Col md={12}>
                        <CardHeader className="buyer-uploads-header space-between">
                            Public Documents
                        </CardHeader>
                        <BuyerUploads
                            accessLevel={
                                MarketplaceBuyerUploadAccessLevel.Public
                            }
                            isReadonly={true}
                        />
                    </Col>
                </Row>
            )}
            {isUploadsExist(MarketplaceBuyerUploadAccessLevel.Private) && (
                <Row>
                    {' '}
                    <Col md={12}>
                        <CardHeader className="buyer-uploads-header space-between">
                            <div className="private-documents-text">
                                <div>Private Documents</div>
                                <InfoTooltip
                                    idText="private-documents-tooltip"
                                    text="These only appear to Sellers which have executed a Data NDA with you."
                                />
                            </div>
                        </CardHeader>
                        <BuyerUploads
                            accessLevel={
                                MarketplaceBuyerUploadAccessLevel.Private
                            }
                            isReadonly={true}
                        />
                    </Col>
                </Row>
            )}
        </>
    );

    return (
        <div className="buyer-profile profile-preview">
            {props.isReadonly ? (
                <></>
            ) : (
                <CardHeader className="profile-preview-header space-between">
                    Buyer Profile
                    <Button
                        color="primary"
                        className="btn"
                        onClick={() => {
                            props.setIsEditMode(true);
                        }}>
                        Edit Profile
                    </Button>
                </CardHeader>
            )}

            {props.showLoiTabs ? (
                <div className="buyer-profile practice-page-tabs m-0">
                    <div className="RRT__container">
                        <div className="RRT__tabs body-tabs">
                            {tabs.map((tab, index) => (
                                <div
                                    key={index}
                                    className={
                                        activeTab === index
                                            ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                            : 'RRT__tab'
                                    }
                                    onClick={() => setActiveTab(index)}>
                                    {tab}
                                    {isBuyerViewer &&
                                        tab === 'Additional NDA Covenants' && (
                                            <InfoTooltip
                                                text={ADDITIONAL_NDA_COVENANTS_BUYER_TOOLTIP}
                                                idText="profile-preview-additional-nda-covenants-tab"
                                                place="top"
                                            />
                                        )}
                                </div>
                            ))}
                        </div>
                    </div>
                    {activeTab === 0 && (
                        <CardBody className="card-body-custom">
                            {profileInfoContent}
                        </CardBody>
                    )}
                    {showLegalIssuesTab &&
                        activeTab === tabs.indexOf('Legal Issues') && (
                            <CardBody>
                                <LegalIssuesForm
                                    legalIssueSections={
                                        legalIssueSectionsFiltered
                                    }
                                    legalIssues={legalIssues}
                                    termForm={loiForm?.termForm}
                                    displayAsLabels={true}
                                    mode={LoiFormMode.BuyerProfile}
                                />
                            </CardBody>
                        )}
                    {showAdditionalCovenantsTab &&
                        activeTab ===
                            tabs.indexOf('Additional NDA Covenants') && (
                            <CardBody>
                                <LegalIssuesForm
                                    legalIssueSections={
                                        additionalCovenantSections
                                    }
                                    legalIssues={additionalCovenants}
                                    termForm={loiForm?.termForm}
                                    displayAsLabels={true}
                                    mode={LoiFormMode.BuyerProfile}
                                />
                            </CardBody>
                        )}
                </div>
            ) : (
                <CardBody className="card-body-custom">
                    {profileInfoContent}
                    {props.showLoiFormsInProfile && legalIssues.length > 0 && (
                        <Row>
                            <Col md={12}>
                                <CardHeader className="buyer-uploads-header space-between">
                                    Legal Issues
                                </CardHeader>
                                <CardBody>
                                    <LegalIssuesForm
                                        legalIssueSections={
                                            legalIssueSectionsFiltered
                                        }
                                        legalIssues={legalIssues}
                                        termForm={loiForm?.termForm}
                                        displayAsLabels={true}
                                        mode={LoiFormMode.BuyerProfile}
                                    />
                                </CardBody>
                            </Col>
                        </Row>
                    )}
                    {props.showLoiFormsInProfile &&
                        additionalCovenants.length > 0 && (
                            <Row>
                                <Col md={12}>
                                    <CardHeader className="buyer-uploads-header space-between">
                                        <span>
                                            Additional NDA Covenants
                                            {isBuyerViewer && (
                                                <InfoTooltip
                                                    text={ADDITIONAL_NDA_COVENANTS_BUYER_TOOLTIP}
                                                    idText="profile-preview-additional-nda-covenants-header"
                                                    place="top"
                                                />
                                            )}
                                        </span>
                                    </CardHeader>
                                    <CardBody>
                                        <LegalIssuesForm
                                            legalIssueSections={
                                                additionalCovenantSections
                                            }
                                            legalIssues={additionalCovenants}
                                            termForm={loiForm?.termForm}
                                            displayAsLabels={true}
                                            mode={LoiFormMode.BuyerProfile}
                                        />
                                    </CardBody>
                                </Col>
                            </Row>
                        )}
                </CardBody>
            )}
        </div>
    );
};
